import { useState } from 'react'
import {
  X, Trash2, Save, TrendingUp, GitBranch, CheckSquare, Loader2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getToken } from '@/lib/auth'

interface WorkingMemory {
  discussed?: string[]
  decided?: string[]
  pending?: string[]
  rejected?: string[]
  open_questions?: string[]
  key_context?: string
}

interface Props {
  sessionId: string
  sessionTitle: string
  onClose: () => void
  onComplete: (outcome: string) => void
}

const API = (path: string, init?: RequestInit) =>
  fetch(`/api/dashboard${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) ?? {}),
    },
  }).then(r => r.json())

const OUTCOMES = [
  {
    id: 'discard',
    label: 'Discard',
    desc: 'Nothing was important enough to keep. Clear the working notes.',
    icon: Trash2,
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20 hover:bg-red-500/15',
  },
  {
    id: 'save',
    label: 'Save as working memory',
    desc: 'Keep the summary available for future sessions. Not permanent yet.',
    icon: Save,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/15',
  },
  {
    id: 'promote',
    label: 'Promote to permanent memory',
    desc: 'Approved decisions ready to commit. Writes to R2 operational memory.',
    icon: TrendingUp,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20 hover:bg-emerald-500/15',
  },
  {
    id: 'architecture',
    label: 'Update architecture',
    desc: 'Something changes the Constitution. Opens the Architecture Workspace.',
    icon: GitBranch,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10 border-indigo-500/20 hover:bg-indigo-500/15',
  },
  {
    id: 'tasks',
    label: 'Create tasks',
    desc: 'Turn pending items into OpsBridge tasks for next session.',
    icon: CheckSquare,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/15',
  },
]

export function SessionOutcomeGate({ sessionId, sessionTitle, onClose, onComplete }: Props) {
  const [step, setStep] = useState<'summarising' | 'choose' | 'done'>('summarising')
  const [summary, setSummary] = useState<WorkingMemory | null>(null)
  const [summaryError, setSummaryError] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Auto-trigger summarise on mount
  useState(() => {
    API(`/sessions/${sessionId}/summarise`, { method: 'POST' })
      .then(r => {
        if (r.success) {
          setSummary(r.summary)
          setStep('choose')
        } else {
          setSummaryError(r.error || 'Could not generate summary')
          setStep('choose')
        }
      })
      .catch(e => {
        setSummaryError(e.message || 'Failed')
        setStep('choose')
      })
  })

  async function handleOutcome(outcomeId: string) {
    setChosen(outcomeId)
    setSaving(true)
    try {
      await API(`/sessions/${sessionId}/outcome`, {
        method: 'PATCH',
        body: JSON.stringify({
          outcome: outcomeId,
          working_memory: summary,
        }),
      })
      setStep('done')
      setTimeout(() => onComplete(outcomeId), 800)
    } catch {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Sheet */}
      <div className="relative w-full sm:max-w-md bg-surface dark:bg-[#0a0f1e] border border-border dark:border-white/10 sm:rounded-2xl rounded-t-2xl p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-text-primary dark:text-white font-semibold text-base">End Session</h2>
            <p className="text-text-muted dark:text-white/40 text-xs mt-0.5 truncate max-w-[260px]">{sessionTitle}</p>
          </div>
          <button type="button" onClick={onClose}
            className="text-text-muted/70 dark:text-white/30 hover:text-text-primary dark:hover:text-white/70 cursor-pointer transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 'summarising' && (
          <div className="flex items-center gap-3 py-8 justify-center">
            <Loader2 className="h-5 w-5 text-indigo-400 animate-spin" />
            <span className="text-text-muted dark:text-white/50 text-sm">Generating session summary...</span>
          </div>
        )}

        {step === 'choose' && (
          <>
            {/* Summary preview */}
            {summary && (
              <div className="bg-surface-elevated/50 dark:bg-white/[0.03] border border-border dark:border-white/[0.08] rounded-xl p-4 mb-4 space-y-2">
                {summary.key_context && (
                  <p className="text-text-secondary dark:text-white/60 text-xs leading-relaxed">{summary.key_context}</p>
                )}
                {summary.pending && summary.pending.length > 0 && (
                  <div>
                    <span className="text-amber-400/70 text-[10px] font-medium uppercase tracking-wider">Pending</span>
                    <ul className="mt-1 space-y-0.5">
                      {summary.pending.slice(0, 3).map((p, i) => (
                        <li key={i} className="text-text-muted dark:text-white/40 text-xs truncate">· {p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {summary.decided && summary.decided.length > 0 && (
                  <div>
                    <span className="text-emerald-400/70 text-[10px] font-medium uppercase tracking-wider">Decided</span>
                    <ul className="mt-1 space-y-0.5">
                      {summary.decided.slice(0, 2).map((d, i) => (
                        <li key={i} className="text-text-muted dark:text-white/40 text-xs truncate">· {d}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {summaryError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 mb-4">
                <p className="text-red-400 text-xs">{summaryError} — choose an outcome anyway.</p>
              </div>
            )}

            {/* Outcome options */}
            <div className="space-y-2">
              <p className="text-text-muted/70 dark:text-white/30 text-[10px] font-medium uppercase tracking-wider mb-3">
                What should happen with today's work?
              </p>
              {OUTCOMES.map(o => (
                <button key={o.id} type="button"
                  onClick={() => handleOutcome(o.id)}
                  disabled={saving}
                  className={cn(
                    'w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all cursor-pointer',
                    chosen === o.id ? 'opacity-50' : '',
                    o.bg
                  )}>
                  <o.icon className={cn('h-4 w-4 mt-0.5 shrink-0', o.color)} />
                  <div className="flex-1 min-w-0">
                    <div className={cn('text-sm font-medium', o.color)}>{o.label}</div>
                    <div className="text-text-muted dark:text-white/40 text-xs mt-0.5">{o.desc}</div>
                  </div>
                  {chosen === o.id && saving && (
                    <Loader2 className="h-3.5 w-3.5 text-text-muted/40 dark:text-white/40 animate-spin shrink-0 mt-0.5" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'done' && (
          <div className="flex items-center gap-3 py-8 justify-center">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckSquare className="h-4 w-4 text-emerald-400" />
            </div>
            <span className="text-text-secondary dark:text-white/60 text-sm">Session outcome saved.</span>
          </div>
        )}
      </div>
    </div>
  )
}
